'use client';

import React, { useRef, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const AuthorStudy: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.06 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale, .reveal-left');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="about" className="py-28 px-6 bg-obsidian-surface relative overflow-hidden">
      <div className="ambient-glow w-[600px] h-[400px] bg-gold/4 bottom-0 right-0" />

      <div className="max-w-7xl mx-auto relative z-10">

        {/* ── About / Bio ── */}
        <div className="mb-16 reveal">
          <span className="text-xs font-body font-semibold text-gold tracking-[0.3em] uppercase mb-4 block">About Judith</span>
          <h2 className="font-display font-light text-parchment leading-tight" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
            The Judith Carey<br />
            <span className="italic text-parchment/50">Advantage.</span>
          </h2>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 auto-rows-auto">

          {/* Large photo */}
          <div className="lg:col-span-5 lg:row-span-2 reveal-scale rounded-2xl overflow-hidden group cursor-pointer relative">
            <AppImage
              src="/assets/images/e8a7a7ca283848ff8b809b4ecc367ef4-1787068022604.jpg"
              alt="Judith Carey, CPRW — Executive Resume Writer and LinkedIn Profile Expert, professional headshot"
              className="w-full h-full object-cover research-photo min-h-[400px]"
              width={700}
              height={900}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-display italic text-parchment text-xl mb-1">Judith Carey, CPRW</p>
              <p className="text-parchment/50 text-xs font-body">Nationally Certified Professional Resume Writer</p>
            </div>
          </div>

          {/* Bio text card */}
          <div className="lg:col-span-7 reveal bg-obsidian-card rounded-2xl p-8 border border-parchment/6">
            <h3 className="font-display font-light text-parchment text-xl mb-4">The Challenge in Today's Market</h3>
            <p className="font-body text-parchment/70 text-base leading-loose mb-6">
              Hiring teams are under pressure to make the right decision quickly. Candidates who require too much interpretation create hesitation.{' '}
              <strong className="font-semibold text-gold">My methodology turns your experience into a clear psychological case for hiring you.</strong>{' '}
              Your achievements become easier to recognize, your leadership easier to trust, and your potential easier to justify to the team and board.
            </p>
            <p className="font-body text-parchment/50 text-sm leading-loose">
              Judith Carey applies a rigorous, strategic methodology designed to win executive interviews — 100% compliant with ATS filters and engineered for a 6-second executive scan, transforming passive duties into measurable revenue, scale, and operational metrics.
            </p>
            <div className="mt-8 pt-6 border-t border-parchment/8 grid grid-cols-3 gap-6">
              {[
                { label: 'ATS Compliant', value: '100%' },
                { label: 'Service Tiers', value: '3' },
                { label: 'Industries', value: 'Multi' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-display font-light text-3xl text-gold mb-1">{stat.value}</p>
                  <p className="text-parchment/35 text-xs font-body uppercase tracking-widest">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process card */}
          <div id="process" className="lg:col-span-4 reveal-scale bg-obsidian-card rounded-2xl p-7 border border-parchment/6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center">
                <Icon name="ArrowPathIcon" size={16} className="text-gold" />
              </div>
              <p className="text-parchment text-sm font-body font-medium">The Collaboration Process</p>
            </div>
            <ul className="space-y-5">
              {[
                { step: '01', title: 'Send Your Materials', text: 'Email your current resume/CV draft along with 1–2 target job descriptions for a complimentary gap analysis.' },
                { step: '02', title: 'Strategic Architecture', text: 'We review the scope, confirm your tier, and begin drafting your high-impact business case.' },
                { step: '03', title: 'Drafting & Polish', text: 'Receive your ATS-optimized deliverables with dedicated revision rounds to ensure perfection.' },
              ].map((item) => (
                <li key={item.step} className="flex gap-4 items-start">
                  <span className="text-gold font-display text-sm font-light shrink-0 mt-0.5 w-6">{item.step}</span>
                  <div>
                    <p className="text-parchment text-sm font-body font-semibold mb-1">{item.title}</p>
                    <p className="text-parchment/50 text-xs font-body leading-relaxed">{item.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Advantage pillars card */}
          <div className="lg:col-span-3 reveal-scale bg-obsidian-card rounded-2xl p-7 border border-parchment/6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center">
                <Icon name="CheckBadgeIcon" size={16} className="text-gold" />
              </div>
              <p className="text-parchment text-sm font-body font-medium">Strategic Pillars</p>
            </div>
            <ul className="space-y-4">
              {[
                { icon: 'MagnifyingGlassIcon', label: 'ATS Search Indexing', desc: 'Keyword density & structural layout to clear recruiter algorithms.' },
                { icon: 'ChartBarIcon', label: 'Quantifiable Impact', desc: 'Revenue growth, cost reduction, and operational scale metrics.' },
                { icon: 'UserGroupIcon', label: 'Leadership Presence', desc: 'Executive scope, board governance, and stakeholder alignment.' },
                { icon: 'DocumentTextIcon', label: 'Executive Design', desc: 'Sophisticated visual hierarchy in Word (.docx) & ATS-safe PDF.' },
              ].map((pillar) => (
                <li key={pillar.label} className="flex gap-3 items-start">
                  <Icon name={pillar.icon as any} size={14} className="text-gold shrink-0 mt-0.5" />
                  <div>
                    <p className="text-parchment/80 text-xs font-body font-semibold mb-0.5">{pillar.label}</p>
                    <p className="text-parchment/40 text-xs font-body leading-relaxed">{pillar.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Quote card */}
          <div className="lg:col-span-12 reveal broadside-quote rounded-2xl p-10 text-center">
            <div className="text-gold/20 font-display text-7xl leading-none mb-3 select-none">"</div>
            <blockquote className="font-display font-light italic text-parchment text-2xl md:text-3xl leading-relaxed max-w-3xl mx-auto">
              A rigorous, strategic methodology designed to win executive interviews — 100% compliant with ATS filters and engineered for a 6-second executive scan.
            </blockquote>
            <cite className="text-parchment/35 text-sm font-body not-italic mt-6 block">
              — Judith Carey, CPRW · Principal Career Strategist
            </cite>
          </div>
        </div>

        {/* ── Consultation ── */}
        <div className="mt-20 reveal">
          <div className="bg-obsidian-card rounded-2xl border border-gold/20 p-10 md:p-14 relative overflow-hidden">
            <div className="ambient-glow w-[400px] h-[300px] bg-gold/5 top-0 right-0 pointer-events-none" />
            <div className="relative z-10">
              <span className="text-xs font-body font-semibold text-gold tracking-[0.3em] uppercase mb-4 block">Consultation</span>
              <h3 className="font-display font-light text-parchment leading-tight mb-4" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)' }}>
                Work Directly<br />
                <span className="italic text-parchment/50">With Judith.</span>
              </h3>
              <p className="font-body text-parchment/70 text-base leading-loose mb-8 max-w-2xl">
                Consultations are <strong className="font-semibold text-gold">paid engagements</strong> — a focused, high-value session tailored to your specific career positioning needs. Due to the depth of attention each client receives, Judith accepts only{' '}
                <strong className="font-semibold text-gold">3 consultation slots per week</strong>. Availability is limited and fills quickly.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                {[
                  { icon: 'CurrencyDollarIcon', label: 'Paid Engagement', desc: 'Investment-grade career strategy session' },
                  { icon: 'CalendarDaysIcon', label: '3 Slots Per Week', desc: 'Limited availability — book early' },
                  { icon: 'UserIcon', label: 'Direct Access', desc: 'Work 1-on-1 with Judith Carey, CPRW' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 bg-obsidian/50 rounded-xl px-5 py-4 border border-parchment/8 flex-1 min-w-[180px]">
                    <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon name={item.icon as any} size={15} className="text-gold" />
                    </div>
                    <div>
                      <p className="text-parchment text-sm font-body font-semibold mb-0.5">{item.label}</p>
                      <p className="text-parchment/45 text-xs font-body leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <a
                href="mailto:jc.careerwriter@gmail.com"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gold text-obsidian font-body font-semibold text-base hover:bg-gold-light transition-all duration-300 hover:shadow-gold group"
              >
                <Icon name="EnvelopeIcon" size={18} />
                Book a Consultation
                <Icon name="ArrowRightIcon" size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuthorStudy;